import { PLAN_CATALOG } from '@secretario/shared';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useLocation } from 'wouter';
import { AI_CONSENT_EVENT, hasAiConsent } from '../components/assistant/AiConsentSheet.js';
import { UsageCard } from '../components/conta/UsageCard.js';
import { ActionRow, LinkRow, ToggleRow, useSetPref } from '../components/conta/rows.js';
import { SaudeRowGate } from '../components/clinica/gate.js';
import { PageShell } from '../components/stage/PageShell.js';
import { useOptionalStage } from '../components/stage/StageProvider.js';
import { IS_FULL } from '../lib/edition.js';
import {
  GlyphBell,
  GlyphChat,
  GlyphLaptop,
  GlyphLifebuoy,
  GlyphLock,
  GlyphMic,
  GlyphShieldCheck,
  GlyphSliders,
  GlyphSmile,
  GlyphSpark,
  GlyphStar,
  GlyphAt,
  GlyphTv,
  GlyphUsers,
} from '../components/ui/Glyphs.js';
import { Skeleton } from '../components/ui/Skeleton.js';
import { logout } from '../lib/api/auth.js';
import { copyText } from '../lib/clipboard.js';
import { inviteLink } from '../lib/invite.js';
import { notifyPermission, type NotifyPermission } from '../lib/notify.js';
import { getEntitlements } from '../lib/plan.js';
import { isDono } from '../lib/api/dono.js';
import { searchSettings } from '../lib/settingsIndex.js';
import './ajustes-busca.css';
import { startTour } from '../lib/tutorial.js';
import { useAccount } from '../lib/useAccount.js';
import { useProfile, useUpdateProfile } from '../lib/useProfile.js';

/**
 * "Conta": o que a pessoa procura nos Ajustes, em grupos curtos (estudo "Core de
 * análises" 2.2 e AGENTS.md regra 13): Você, Seu Secretário, Notificações, Telas e
 * aparelhos, Privacidade, Plano e uso, Ajuda e conta. No máximo quatro linhas por
 * grupo; o resto fica na página do grupo. Tudo em frase do Secretário.
 */
export function AjustesPage() {
  const [, navigate] = useLocation();
  const accountQ = useAccount();
  const profileQ = useProfile();
  const update = useUpdateProfile();
  const stage = useOptionalStage();
  // Painel do dono: a entrada só aparece para a conta do dono (o servidor decide).
  const donoQ = useQuery({ queryKey: ['dono', 'acesso'], queryFn: isDono, staleTime: 3_600_000 });
  const entitlementsQ = useQuery({
    queryKey: ['entitlements'],
    queryFn: getEntitlements,
    staleTime: 30_000,
  });
  const { saving, setPref } = useSetPref();

  const profile = profileQ.data ?? null;
  const prefs = profile?.preferences ?? {};
  const agentName = profile?.secretaryName?.trim() || 'seu Secretário';
  // Começo de frase: nome próprio ou "Seu Secretário".
  const agentFirst = agentName.charAt(0).toUpperCase() + agentName.slice(1);
  const fullName = (profile?.nickname || accountQ.data?.user.displayName || '').trim();
  const first = fullName.split(/\s+/)[0] || '';
  const username = accountQ.data?.user.username ?? '';
  const phoneHint = accountQ.data?.user.phoneHint ?? '';
  const devices = accountQ.data?.devices.length ?? 0;

  // ── Você agora ──
  const busy = (profile?.ownerStatus ?? 'disponivel') === 'ocupado';
  const [savingStatus, setSavingStatus] = useState(false);
  async function setStatus(next: 'disponivel' | 'ocupado') {
    if ((next === 'ocupado') === busy || savingStatus) return;
    setSavingStatus(true);
    try {
      await update.mutateAsync({ ownerStatus: next });
    } catch {
      /* silencioso */
    } finally {
      setSavingStatus(false);
    }
  }

  // ── Convidar ──
  const refLink = username ? inviteLink(username) : '';
  const [inviteNote, setInviteNote] = useState('');
  async function invite() {
    if (!refLink) return;
    const text =
      'Conheci um secretário pessoal que lembra das contas, organiza a agenda e resolve as coisas por mim. Dá uma olhada:';
    try {
      if (navigator.share) {
        await navigator.share({ title: 'Secretário', text, url: refLink });
        return;
      }
    } catch {
      return;
    }
    try {
      await copyText(refLink);
      setInviteNote('Link copiado. É só mandar para quem você quiser.');
    } catch {
      setInviteNote(`Seu link: ${refLink.replace(/^https?:\/\//, '')}`);
    }
  }

  // ── Avisos ──
  const notifications = {
    messages: true,
    reminders: true,
    secretary: true,
    ...((prefs.notifications as Record<string, boolean> | undefined) ?? {}),
  };
  const [devicePermission, setDevicePermission] = useState<NotifyPermission>('unsupported');
  useEffect(() => {
    void notifyPermission().then(setDevicePermission);
  }, []);

  // ── Privacidade ──
  const aiAllowed = hasAiConsent(prefs);
  const discoverable = prefs.discoverable !== false;

  // ── Plano ──
  const status = entitlementsQ.data ?? null;
  const plan = status?.plan ?? 'free';
  const planName = PLAN_CATALOG[plan].name;
  const planHint =
    plan === 'free'
      ? 'Grátis, com amostras'
      : `R$ ${(PLAN_CATALOG[plan].price / 100).toFixed(2).replace('.', ',')} por mês`;

  const loading = profileQ.isLoading || accountQ.isLoading;
  // Busca nos Ajustes: com texto, a lista de resultados toma o lugar dos grupos.
  const [query, setQuery] = useState('');

  return (
    <PageShell
      kicker="Conta"
      title={first ? `Tudo no seu jeito, ${first}` : 'Tudo no seu jeito'}
      onBack={() => navigate('/')}
      backLabel="Voltar para a tela inicial"
    >
      {loading ? (
        <Skeleton variant="rows" label="Abrindo a sua conta" />
      ) : (
        <>
          <SettingsSearch
            query={query}
            onQuery={setQuery}
            onPick={(href) => {
              const group = href.startsWith('/ajustes#') ? href.slice('/ajustes#'.length) : null;
              if (!group) {
                navigate(href);
                return;
              }
              // Ajuste que mora nesta página: limpa a busca e rola até o grupo dele.
              setQuery('');
              requestAnimationFrame(() =>
                document
                  .querySelector(`[data-group="${group}"]`)
                  ?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
              );
            }}
          />
          <div hidden={query.trim() !== ''}>
            {donoQ.data === true && (
              <section className="pgs-section" aria-label="Dono">
                <h2 className="pgs-h2">Dono</h2>
                <LinkRow
                  href="/dono"
                  icon={<GlyphStar />}
                  title="Painel do dono"
                  hint="Cadastros, assinaturas, custos e a saúde do app"
                />
              </section>
            )}
            <section className="pgs-section" aria-label="Você" data-group="voce">
              <h2 className="pgs-h2">Você</h2>
              <SaudeRowGate />
              <LinkRow
                href="/perfil"
                icon={<GlyphSmile />}
                title={fullName || 'Seu perfil'}
                hint={[username ? `@${username}` : '', phoneHint].filter(Boolean).join(' · ')}
              />
              <div className="ct-status" role="group" aria-label="Você agora">
                <small>
                  {busy
                    ? `${agentFirst} sabe que você está ocupado e segura o que der.`
                    : `${agentFirst} pode te acionar quando precisar.`}
                </small>
                <div className="pgs-chips">
                  <button
                    type="button"
                    className={`pgs-chip${busy ? '' : ' on'}`}
                    onClick={() => void setStatus('disponivel')}
                    disabled={savingStatus}
                  >
                    Disponível
                  </button>
                  <button
                    type="button"
                    className={`pgs-chip busy${busy ? ' on' : ''}`}
                    onClick={() => void setStatus('ocupado')}
                    disabled={savingStatus}
                  >
                    Ocupado
                  </button>
                </div>
              </div>
              <LinkRow
                href="/ajustes/seguranca"
                icon={<GlyphLock />}
                title="Segurança"
                hint="Passkey, autenticador, e-mail de recuperação e aparelhos"
              />
              <ActionRow
                onClick={() => void invite()}
                icon={<GlyphUsers />}
                title="Convidar amigos"
                hint={inviteNote || 'Todo mundo merece um secretário. Mando o seu link.'}
                disabled={!refLink}
              />
            </section>

            <section className="pgs-section" aria-label="Seu Secretário" data-group="secretario">
              <h2 className="pgs-h2">Seu Secretário</h2>
              <LinkRow
                href="/mudar-jeito"
                icon={<GlyphSpark />}
                title="Mudar o jeito"
                hint="Rosto, voz e nome"
              />
              <LinkRow
                href="/ajustes/secretario"
                icon={<GlyphMic />}
                title="Jeito de falar e de chamar"
                hint="Respostas, Deixar na mesa e o assistente do celular"
              />
              <LinkRow
                href="/ajustes/inicio"
                icon={<GlyphSliders />}
                title="Tela inicial"
                hint="Como abre, em que dia e os atalhos"
              />
              <ActionRow
                onClick={() => {
                  startTour();
                  navigate('/');
                }}
                icon={<GlyphStar />}
                title="Trilha de aprendizagem"
                hint={`Os primeiros passos no app, com ${agentName}`}
              />
            </section>

            <section className="pgs-section" aria-label="Notificações" data-group="notificacoes">
              <h2 className="pgs-h2">Notificações</h2>
              {(devicePermission === 'prompt' || devicePermission === 'denied') && (
                <LinkRow
                  href="/ajustes/avisos"
                  icon={<GlyphBell />}
                  title="Falta liberar as notificações neste aparelho"
                  hint="Sem isso, nada do que eu mando chega."
                />
              )}
              {(
                [
                  ['messages', 'Mensagens novas', 'Notificação quando alguém te escreve'],
                  ['reminders', 'Lembretes e agenda', 'Na hora e antes dos compromissos'],
                  [
                    'secretary',
                    `Notificações de ${agentName}`,
                    'Quando eu precisar de uma decisão sua',
                  ],
                ] as const
              ).map(([key, title, hint]) => (
                <ToggleRow
                  key={key}
                  on={notifications[key]}
                  onToggle={() =>
                    void setPref(
                      { notifications: { ...notifications, [key]: !notifications[key] } },
                      `notif-${key}`,
                    )
                  }
                  title={title}
                  hint={hint}
                  disabled={saving === `notif-${key}`}
                />
              ))}
              <LinkRow
                href="/ajustes/avisos"
                icon={<GlyphBell />}
                title="Mais sobre notificações"
                hint="O texto na notificação, mensagens de outros apps e lembretes garantidos"
              />
            </section>

            <section className="pgs-section" aria-label="Conexões">
              <h2 className="pgs-h2">Conexões</h2>
              <LinkRow
                href="/ajustes/sua-ia"
                icon={<GlyphSpark />}
                title="Sua IA"
                hint="Gemini, ChatGPT ou Claude, com a sua própria conta"
                {...(plan === 'pro' ? {} : { tag: 'Completo' })}
              />
              <LinkRow
                href="/email"
                icon={<GlyphAt />}
                title="E-mail"
                hint="Encaminhe e-mails para mim e receba o resumo aqui"
              />
              <LinkRow
                href="/ajustes/telas"
                icon={<GlyphTv />}
                title="Telas conectadas"
                hint="TV e notebook para apresentar e ver o seu dia"
              />
              <LinkRow
                href="/aparelhos"
                icon={<GlyphLaptop />}
                title="Aparelhos com a sua conta"
                hint={
                  devices === 1
                    ? 'Este celular é o único conectado'
                    : `${devices} celulares e computadores conectados`
                }
              />
              {IS_FULL && (
                <LinkRow
                  href="/whatsapp"
                  icon={<GlyphChat />}
                  title="WhatsApp"
                  hint="Enviar e responder pelo seu WhatsApp"
                />
              )}
            </section>

            <section className="pgs-section" aria-label="Privacidade" data-group="privacidade">
              <h2 className="pgs-h2">Privacidade</h2>
              <ToggleRow
                on={aiAllowed}
                onToggle={() =>
                  // Ligar passa pela folha de permissão; desligar vale na hora.
                  aiAllowed
                    ? void setPref({ aiConsent: null }, 'ia')
                    : window.dispatchEvent(new Event(AI_CONSENT_EVENT))
                }
                icon={<GlyphSpark />}
                title="Usar inteligência artificial"
                hint={
                  aiAllowed
                    ? 'Ligada: é assim que eu entendo e respondo seus pedidos'
                    : 'Desligada: nada seu vai para serviços de IA'
                }
                disabled={saving === 'ia'}
              />
              <ActionRow
                onClick={() => (stage ? stage.show({ kind: 'notebook' }) : navigate('/sobre-voce'))}
                icon={<GlyphLock />}
                title="O que eu sei de você"
                hint="Veja, corrija ou apague cada coisa que eu lembro"
              />
              <ToggleRow
                on={discoverable}
                onToggle={() => void setPref({ discoverable: !discoverable }, 'discover')}
                icon={<GlyphUsers />}
                title="Aparecer pra quem tem meu número"
                hint="Quem já te salvou nos contatos vê que você está aqui"
                disabled={saving === 'discover'}
              />
              <LinkRow
                href="/ajustes/privacidade"
                icon={<GlyphShieldCheck />}
                title="Mais privacidade"
                hint="Leitura, online, pesquisa e os seus dados"
              />
            </section>

            <section className="pgs-section" aria-label="Plano e uso">
              <h2 className="pgs-h2">Plano e uso</h2>
              <LinkRow
                href="/plano"
                icon={<GlyphStar />}
                title={`Plano ${planName}`}
                hint={status ? planHint : 'Assinatura e uso'}
                tag={plan === 'free' ? 'Ver planos' : 'Gerenciar'}
              />
              {status && <UsageCard status={status} />}
            </section>

            <section className="pgs-section" aria-label="Ajuda e conta" data-group="conta">
              <h2 className="pgs-h2">Ajuda e conta</h2>
              <LinkRow
                href="/ajuda"
                icon={<GlyphLifebuoy />}
                title="Ajuda e sobre"
                hint="Dúvidas comuns e como falar com a gente"
              />
              <LinkRow
                href="/privacidade"
                icon={<GlyphShieldCheck />}
                title="Privacidade e termos"
                hint="O que eu guardo, para quê e como apagar"
              />
              <ActionRow
                onClick={() => void logout()}
                icon={<GlyphLaptop />}
                title="Sair desta conta"
                hint={username ? `Conectado como @${username}` : undefined}
              />
              <LinkRow href="/ajustes/apagar" title="Apagar minha conta" quiet />
            </section>

            <p className="ct-foot">Secretário · feito pra te dar tempo de volta</p>
          </div>
        </>
      )}
    </PageShell>
  );
}

/** Busca nos Ajustes: procura em todos os grupos e subpáginas (lib/settingsIndex.ts). */
function SettingsSearch({
  query,
  onQuery,
  onPick,
}: {
  query: string;
  onQuery: (value: string) => void;
  onPick: (href: string) => void;
}) {
  const results = searchSettings(query);
  const typing = query.trim() !== '';
  return (
    <div className="aj-search-wrap">
      <label className="aj-search">
        <svg className="aj-search-ic" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
          <circle cx="11" cy="11" r="7" fill="none" stroke="currentColor" strokeWidth="2" />
          <line
            x1="16.2"
            y1="16.2"
            x2="21"
            y2="21"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
        <input
          type="search"
          className="aj-search-input"
          placeholder="Buscar nos ajustes"
          aria-label="Buscar nos ajustes"
          value={query}
          onChange={(event) => onQuery(event.target.value)}
          enterKeyHint="search"
        />
        {typing && (
          <button
            type="button"
            className="aj-search-clear"
            aria-label="Limpar a busca"
            onClick={() => onQuery('')}
          >
            ×
          </button>
        )}
      </label>
      {typing && (
        <section
          className="pgs-section aj-results"
          aria-label="Resultados da busca"
          aria-live="polite"
        >
          {results.length ? (
            results.map((entry) => (
              <button
                key={`${entry.href}-${entry.title}`}
                type="button"
                className="aj-result"
                onClick={() => onPick(entry.href)}
              >
                <small>{entry.group}</small>
                <b>{entry.title}</b>
                <span>{entry.hint}</span>
              </button>
            ))
          ) : (
            <p className="aj-empty">
              Não achei "{query.trim()}" nos ajustes. Tente outra palavra, ou me peça pela conversa.
            </p>
          )}
        </section>
      )}
    </div>
  );
}
